'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';

import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import { createIntakeLead } from '@/utils/intakeLeadsService';
import { sendNewIntakeEmail } from '@/utils/emailService';


interface PlanOption {
  name: string;
  price: number;
  frequency: string;
  displayPrice: string;
  serviceDescription: string;
}

const planOptions: PlanOption[] = [
  {
    name: 'Bi-Weekly Maintenance',
    price: 350,
    frequency: 'bi_weekly',
    displayPrice: '$350/month',
    serviceDescription: 'Bi-weekly recurring home maintenance service',
  },
  {
    name: 'Weekly Maintenance (Most Popular)',
    price: 700,
    frequency: 'weekly',
    displayPrice: '$700/month',
    serviceDescription: 'Weekly recurring home maintenance service',
  },
  {
    name: 'Premium Care',
    price: 1200,
    frequency: 'monthly',
    displayPrice: '$1,200/month',
    serviceDescription: 'Weekly recurring service with premium attention',
  },
];

const homeSizeOptions = [
  { label: 'Studio / 1 Bedroom', value: '1' },
  { label: '2 Bedrooms', value: '2' },
  { label: '3 Bedrooms', value: '3' },
  { label: '4+ Bedrooms', value: '4+' },
];

const bathroomOptions = [
  { label: '1 Bathroom', value: '1' },
  { label: '1.5 Bathrooms', value: '1.5' },
  { label: '2 Bathrooms', value: '2' },
  { label: '2.5 Bathrooms', value: '2.5' },
  { label: '3+ Bathrooms', value: '3+' },
];

const squareFootageOptions = [
  { label: 'Under 1,000 sq ft', value: '< 1000' },
  { label: '1,000 - 1,500 sq ft', value: '1000-1500' },
  { label: '1,500 - 2,000 sq ft', value: '1500-2000' },
  { label: '2,000 - 2,500 sq ft', value: '2000-2500' },
  { label: '2,500+ sq ft', value: '> 2500' },
];

const homeConditionOptions = [
  { label: 'Excellent - Very tidy, minimal cleaning needed', value: 'excellent' },
  { label: 'Good - Normal maintenance required', value: 'good' },
  { label: 'Fair - Moderate cleaning needed', value: 'fair' },
  { label: 'Needs Attention - Deep cleaning recommended', value: 'needs_attention' },
];

export default function CustomerApplicationFormPage() {
  const router = useRouter();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    bedrooms: '',
    bathrooms: '',
    squareFootage: '',
    homeCondition: '',
    selectedPlan: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    const phoneRegex = /^\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.address.trim()) newErrors.address = 'Street address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!zipRegex.test(formData.zipCode)) {
      newErrors.zipCode = 'Please enter a valid ZIP code';
    }

    if (!formData.bedrooms) newErrors.bedrooms = 'Please select number of bedrooms';
    if (!formData.bathrooms) newErrors.bathrooms = 'Please select number of bathrooms';
    if (!formData.squareFootage) newErrors.squareFootage = 'Please select approximate square footage';
    if (!formData.homeCondition) newErrors.homeCondition = 'Please select home condition';
    if (!formData.selectedPlan) newErrors.selectedPlan = 'Please select a cleaning plan';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      setErrorMessage('Please fix the errors above before submitting');
      return;
    }

    setSubmitStatus('loading');
    setErrorMessage('');

    const selectedPlanData = planOptions.find(p => p.name === formData.selectedPlan);
    if (!selectedPlanData) {
      setSubmitStatus('error');
      setErrorMessage('Invalid plan selected');
      return;
    }

    const fullAddress = `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}`;

    const { data, error } = await createIntakeLead({
      contact_first_name: formData.firstName,
      contact_last_name: formData.lastName,
      contact_email: formData.email,
      contact_phone: formData.phone,
      address: fullAddress,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms,
      square_footage: formData.squareFootage,
      property_type: formData.homeCondition,
      frequency: selectedPlanData.frequency,
      monthly_rate: selectedPlanData.price,
      service_type: 'recurring',
    });

    if (error) {
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to submit application. Please try again.');
    } else {
      const emailResult = await sendNewIntakeEmail({
        businessOwnerEmail: 'info@ordifyclean.com',
        applicantName: `${formData.firstName} ${formData.lastName}`,
        selectedPlan: `${selectedPlanData.name} - ${selectedPlanData.displayPrice}`,
        homeAddress: fullAddress,
        homeCondition: formData.homeCondition
      });

      if (!emailResult.success) {
        console.error('Failed to send email notification:', emailResult.error);
      }

      router.push('/application-received');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl mb-4">
              Customer Application Form
            </h1>
            <p className="text-lg text-muted-foreground mb-2">
              This application helps us determine the right plan and match you with a vetted cleaning professional.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Your application is reviewed by our team before service begins.
            </p>
          </div>

          {/* Application Flow Steps */}
          <div className="bg-card border border-border rounded-lg p-6 mb-10">
            <h2 className="text-base font-headline font-semibold text-foreground mb-4 text-center">
              How It Works
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { step: '1', label: 'Submit application' },
                { step: '2', label: 'We review & approve' },
                { step: '3', label: 'You receive payment link' },
                { step: '4', label: 'Service begins' },
              ].map((item) => (
                <div key={item.step} className="flex flex-col items-center text-center">
                  <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                    <span className="text-sm font-semibold text-primary">{item.step}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-8 p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-1">
                    Application Submitted Successfully!
                  </h3>
                  <p className="text-green-700 dark:text-green-300">
                    Thank you for your application. Our team will review your submission and contact you within 1–2 business days to confirm your service details and schedule.
                  </p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && errorMessage && (
            <div className="mb-8 p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-1">
                    Submission Error
                  </h3>
                  <p className="text-red-700 dark:text-red-300">{errorMessage}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Personal Information */}
            <section className="bg-card border border-border rounded-lg p-8">
              <h2 className="text-2xl font-headline font-semibold text-foreground mb-6 pb-3 border-b border-border">
                Personal Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                      errors.firstName ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="John"
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                      errors.lastName ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="Doe"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                      errors.email ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="john.doe@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                      errors.phone ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="(555) 123-4567"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Address Information */}
            <section className="bg-card border border-border rounded-lg p-8">
              <h2 className="text-2xl font-headline font-semibold text-foreground mb-6 pb-3 border-b border-border">
                Address Information
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-foreground mb-2">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                      errors.address ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="123 Main Street, Apt 4B"
                  />
                  {errors.address && (
                    <p className="text-red-500 text-sm mt-1">{errors.address}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-foreground mb-2">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                        errors.city ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="New York"
                    />
                    {errors.city && (
                      <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-foreground mb-2">
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                        errors.state ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="NY"
                      maxLength={2}
                    />
                    {errors.state && (
                      <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="zipCode" className="block text-sm font-medium text-foreground mb-2">
                      ZIP Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="zipCode"
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                        errors.zipCode ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="10001"
                      maxLength={10}
                    />
                    {errors.zipCode && (
                      <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Home Details */}
            <section className="bg-card border border-border rounded-lg p-8">
              <h2 className="text-2xl font-headline font-semibold text-foreground mb-6 pb-3 border-b border-border">
                Home Details
              </h2>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="bedrooms" className="block text-sm font-medium text-foreground mb-2">
                      Number of Bedrooms <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="bedrooms"
                      value={formData.bedrooms}
                      onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                        errors.bedrooms ? 'border-red-500' : 'border-border'
                      }`}
                    >
                      <option value="">Select bedrooms</option>
                      {homeSizeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.bedrooms && (
                      <p className="text-red-500 text-sm mt-1">{errors.bedrooms}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="bathrooms" className="block text-sm font-medium text-foreground mb-2">
                      Number of Bathrooms <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="bathrooms"
                      value={formData.bathrooms}
                      onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                        errors.bathrooms ? 'border-red-500' : 'border-border'
                      }`}
                    >
                      <option value="">Select bathrooms</option>
                      {bathroomOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.bathrooms && (
                      <p className="text-red-500 text-sm mt-1">{errors.bathrooms}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="squareFootage" className="block text-sm font-medium text-foreground mb-2">
                    Approximate Square Footage <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="squareFootage"
                    value={formData.squareFootage}
                    onChange={(e) => handleInputChange('squareFootage', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                      errors.squareFootage ? 'border-red-500' : 'border-border'
                    }`}
                  >
                    <option value="">Select square footage</option>
                    {squareFootageOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.squareFootage && (
                    <p className="text-red-500 text-sm mt-1">{errors.squareFootage}</p>
                  )}
                  <p className="text-sm text-muted-foreground mt-1.5">
                    Pricing tiers are designed for homes up to 2,500 sq ft. Larger homes may require a custom plan during application review.
                  </p>
                </div>

                <div>
                  <label htmlFor="homeCondition" className="block text-sm font-medium text-foreground mb-2">
                    Home Condition Assessment <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="homeCondition"
                    value={formData.homeCondition}
                    onChange={(e) => handleInputChange('homeCondition', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                      errors.homeCondition ? 'border-red-500' : 'border-border'
                    }`}
                  >
                    <option value="">Select condition</option>
                    {homeConditionOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.homeCondition && (
                    <p className="text-red-500 text-sm mt-1">{errors.homeCondition}</p>
                  )}
                  <p className="text-sm text-muted-foreground mt-2">
                    This helps us understand your cleaning needs and ensure we provide the right level of service.
                  </p>
                </div>
              </div>
            </section>

            {/* Plan Selection */}
            <section className="bg-card border border-border rounded-lg p-8">
              <h2 className="text-2xl font-headline font-semibold text-foreground mb-6 pb-3 border-b border-border">
                Plan Selection
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-4">
                  Choose Your Preferred Plan <span className="text-red-500">*</span>
                </label>
                
                <div className="space-y-4">
                  {planOptions.map(plan => (
                    <div
                      key={plan.name}
                      onClick={() => handleInputChange('selectedPlan', plan.name)}
                      className={`relative border rounded-lg p-6 cursor-pointer transition-all duration-300 ${
                        formData.selectedPlan === plan.name
                          ? 'border-primary bg-primary/5 ring-2 ring-primary' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id={`plan-${plan.name}`}
                          name="selectedPlan"
                          value={plan.name}
                          checked={formData.selectedPlan === plan.name}
                          onChange={(e) => handleInputChange('selectedPlan', e.target.value)}
                          className="w-5 h-5 text-primary focus:ring-2 focus:ring-primary"
                        />
                        <div className="ml-4 flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-foreground">
                              {plan.name}
                            </h3>
                            <span className="text-xl font-bold text-primary">
                              {plan.displayPrice}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {plan.serviceDescription}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {errors.selectedPlan && (
                  <p className="text-red-500 text-sm mt-2">{errors.selectedPlan}</p>
                )}
                
                <p className="text-sm text-muted-foreground mt-4">
                  All plans are subject to approval. Payment is arranged after your application is reviewed.
                </p>
              </div>
            </section>

            {/* Submit Button */}
            <div className="flex flex-col items-center gap-4 pt-6">
              <p className="text-center text-sm text-muted-foreground max-w-2xl">
                Homes requiring deep initial cleaning may need a one-time Initial Reset Service before recurring visits begin.
              </p>
              <p className="text-center text-sm text-muted-foreground max-w-2xl">
                Recurring cleaning subscriptions reserve dedicated time in our schedule each month. Missed or skipped visits do not qualify for refunds. Rescheduling requires at least 24 hours&apos; notice and is subject to availability.
              </p>
              <p className="text-center text-sm text-muted-foreground max-w-2xl font-medium">
                Submitting this application does not require payment. You will only be charged after approval and plan confirmation.
              </p>
              <button
                type="submit"
                disabled={submitStatus === 'loading'}
                className={`w-full md:w-auto px-12 py-4 bg-primary text-primary-foreground rounded-md font-cta font-semibold text-lg transition-all duration-300 ${
                  submitStatus === 'loading' ?'opacity-50 cursor-not-allowed' :'hover:bg-primary/90 hover:shadow-lg'
                }`}
              >
                {submitStatus === 'loading' ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting Application...
                  </span>
                ) : (
                  'Submit Application'
                )}
              </button>
              
              <p className="text-center text-sm text-muted-foreground font-medium">
                No payment is required at this stage
              </p>
              <p className="text-center text-xs text-muted-foreground/70">
                Most applicants hear back within 24–48 hours.
              </p>

              <p className="text-center text-sm text-muted-foreground max-w-2xl">
                By submitting this application, you agree to our approval-based service model.
                Our team will review your application and contact you within 1–2 business days.
              </p>
            </div>
          </form>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}